// Drawer + desktop UI only:
// - drawer track highlighting
// - now-playing shortcut
// - desktop bar rendering + download modal
// ============================================================
// SV Audio Player — Drawer UI (desktop)
// ============================================================
//
// This module owns DOM manipulation for:
// - the drawer playlist UI
// - the "now playing" shortcut
// - the desktop bar
//
// It intentionally talks to globals (like window.SVPlayer) and
// DOM attributes, not internal closure state.

/**
 * Debug helper for drawer UI.
 * If you already have a similar helper elsewhere, it's fine to
 * duplicate it here for now.
 */
function svDebug(tag, payload) {
  try {
    const enabled =
      typeof window !== "undefined" && window.__SV_DEBUG__ === true;
    if (!enabled) return;
    // eslint-disable-next-line no-console
    console.log("[SVDEBUG]", tag, payload);
  } catch {
    // never throw from debug
  }
}

// ------------------------------------------------------------
// Exported drawer helpers
// ------------------------------------------------------------
export function renderDrawerPlaylist(tracksArg) {
  /**
   * Render the current queue into the drawer.
   * If tracksArg is provided, use it; otherwise ask SVPlayer for its playlist.
   */
  const drawerRoot = document.querySelector("[data-sv-player-drawer]");
  if (!drawerRoot) return;

  let tracks = Array.isArray(tracksArg) ? tracksArg : null;

  // If no explicit tracks were passed, fall back to the public SVPlayer API
  if (!tracks) {
    const SVPlayer = window.SVPlayer;
    if (
      SVPlayer &&
      typeof SVPlayer.getPlaylist === "function"
    ) {
      try {
        tracks = SVPlayer.getPlaylist() || [];
      } catch (err) {
        console.warn("[SVPlayer] Drawer: getPlaylist() failed:", err);
        tracks = [];
      }
    } else {
      tracks = [];
    }
  }

  drawerRoot.innerHTML = "";

  if (!Array.isArray(tracks) || tracks.length === 0) {
    return;
  }

  const frag = document.createDocumentFragment();

  tracks.forEach((track, index) => {
    if (!track) return;

    const rawId =
      track.drawerId || track.id || track.slug || track.songSlug || "";
    const id = String(rawId || "").toLowerCase();
    if (!id) return;

    const li = document.createElement("li");
    li.className = "drawer-track-item";
    li.setAttribute("data-sv-drawer-track-id", id);
    li.setAttribute("data-sv-drawer-index", String(index));

    // Play button inside the drawer row
    const playBtn = document.createElement("button");
    playBtn.type = "button";
    playBtn.className = "drawer-track-play";
    playBtn.setAttribute("data-sv-drawer-play", "true");

    const labelBase = track.songTitle || track.title || track.name || id;
    const ariaLabel = labelBase ? `Play ${labelBase}` : "Play";
    playBtn.setAttribute("aria-label", ariaLabel);
    playBtn.textContent = "▶";

    // Link to the song page
    const link = document.createElement("a");
    link.className = "drawer-track-link";
    link.href = `/music/${encodeURIComponent(id)}`;

    const titleSpan = document.createElement("span");
    titleSpan.className = "drawer-track-title";
    titleSpan.textContent = labelBase || id;
    link.appendChild(titleSpan);

    // Meta: album title + (optional) real track number
    const albumTitle = track.albumTitle || track.album || "";
    const trackNumber =
      typeof track.trackNumber === "number" && track.trackNumber > 0
        ? track.trackNumber
        : null;

    const bits = [];
    if (trackNumber != null) {
      bits.push(String(trackNumber));
    }
    if (albumTitle) {
      bits.push(albumTitle);
    }

    if (bits.length) {
      const metaSpan = document.createElement("span");
      metaSpan.className = "drawer-track-meta";
      metaSpan.textContent = bits.join(" • ");
      link.appendChild(metaSpan);
    }

    li.appendChild(playBtn);
    li.appendChild(link);
    frag.appendChild(li);
  });

  drawerRoot.appendChild(frag);

  // Re-apply the "current" highlight if something is already playing
  const currentTrack =
    window.SVPlayer && typeof window.SVPlayer.getCurrentTrack === "function"
      ? window.SVPlayer.getCurrentTrack()
      : null;

  updateDrawerFromTrackUi(currentTrack);
}


export function initDrawerInteractions() {
  const drawerRoot = document.querySelector("[data-sv-player-drawer]");
  if (!drawerRoot) return;

  drawerRoot.addEventListener("click", (event) => {
    const btn = event.target.closest('[data-sv-drawer-play="true"]');
    if (!btn) return;

    // Respect modified clicks (cmd/ctrl/etc)
    if (
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    const item = btn.closest("[data-sv-drawer-index]");
    if (!item) return;

    const rawIndex = item.getAttribute("data-sv-drawer-index");
    const index = parseInt(rawIndex, 10);
    if (!Number.isFinite(index) || index < 0) return;

    const SVPlayer = window.SVPlayer;
    if (
      !SVPlayer ||
      typeof SVPlayer.getPlaylist !== "function" ||
      typeof SVPlayer.loadTrack !== "function"
    ) {
      console.warn("[SVPlayer] Drawer: SVPlayer API is not ready");
      return;
    }

    let playlist;
    try {
      playlist = SVPlayer.getPlaylist() || [];
    } catch (err) {
      console.warn("[SVPlayer] Drawer: getPlaylist() failed:", err);
      return;
    }

    if (!Array.isArray(playlist) || playlist.length === 0) return;
    if (index >= playlist.length) return;

    const track = playlist[index];
    if (!track || !track.audioUrl) {
      console.warn(
        "[SVPlayer] Drawer: missing track/audioUrl for index",
        index,
        track
      );
      return;
    }

    // Use the public API so all invariants stay in sync
    try {
      SVPlayer.loadTrack(track, { autoplay: true, reset: true });
    } catch (err) {
      console.warn("[SVPlayer] Drawer: loadTrack() failed:", err);
    }
  });
}

export function updateDrawerFromTrackUi(track) {
  // const drawerRoot = document.querySelector("[data-sv-player-drawer]");
  // if (!drawerRoot) return;

  //DEBUG
  const drawerRoot = document.querySelector("[data-sv-player-drawer]");
  if (!drawerRoot) {
    svDebug("updateDrawerFromTrackUi:noDrawer", {
      trackId: track && track.id,
      slug: track && (track.slug || track.songSlug),
    });
    return;
  }

  const items = drawerRoot.querySelectorAll("[data-sv-drawer-track-id]");

  const activeId =
    track &&
    (track.drawerId || track.id || track.slug || track.songSlug || null);

  const activeIdStr = activeId != null ? String(activeId).toLowerCase() : null;

  items.forEach((item) => {
    const itemId = String(
      item.getAttribute("data-sv-drawer-track-id") || ""
    ).toLowerCase();

    const isCurrent = !!activeIdStr && itemId === activeIdStr;

    item.classList.toggle("is-current", isCurrent);

    if (isCurrent) {
      item.setAttribute("aria-current", "true");
    } else if (item.hasAttribute("aria-current")) {
      item.removeAttribute("aria-current");
    }
  });
}

export function updateNowPlayingShortcut(track) {
  const wrapper = document.querySelector("[data-sv-now-playing-shortcut]");
  if (!wrapper) return;

  const link = wrapper.querySelector("[data-sv-now-playing-link]");
  const titleEl = wrapper.querySelector("[data-sv-now-playing-title]");
  if (!link || !titleEl) return;

  if (!track) {
    wrapper.hidden = true;
    return;
  }

  const slugRaw =
    track.slug || track.songSlug || track.id || track.drawerId || null;

  if (!slugRaw) {
    wrapper.hidden = true;
    return;
  }

  const slug = String(slugRaw);
  const href = `/music/${encodeURIComponent(slug)}`;

  // Optional: if we're already on this song's page, don't show the shortcut
  let isCurrentPage = false;
  try {
    const currentPath = window.location.pathname.replace(/\/+$/, "");
    const targetPath = href.replace(/\/+$/, "");
    isCurrentPage = currentPath === targetPath;
  } catch {
    // ignore
  }

  if (isCurrentPage) {
    wrapper.hidden = true;
    return;
  }

  const label = track.songTitle || track.title || track.name || slug;

  link.href = href;
  titleEl.textContent = label;
  wrapper.hidden = false;
}

export function initDesktopBar({
  prevBtn,
  playPauseBtn,
  nextBtn,
  links = {},
  availability = {},
}) {
  if (window.innerWidth < 769) return; // desktop only

  if (document.querySelector(".desktop-bar")) {
    console.log("[Player] initDesktopBar: already present, skipping");
    return;
  }

  console.log("[Player] initDesktopBar: building desktop bar");

  const controlsContainer = document.querySelector(".player-controls");
  const controls = document.querySelector(".controls");
  if (!controlsContainer || !controls) {
    console.warn(
      "[Player] initDesktopBar: missing .player-controls or .controls"
    );
    return;
  }

  // Optional hints (may be null on /music)
  const titleEl = document.getElementById("songTitle");
  const artEl = document.getElementById("albumArt");

  const initialTitle =
    (titleEl && titleEl.textContent && titleEl.textContent.trim()) ||
    "Nothing playing";

  const desktopBar = document.createElement("div");
  desktopBar.className = "desktop-bar";

  desktopBar.innerHTML = `
    <div class="desktop-left">
      <div class="desktop-art-slot" data-sv-player-art></div>
      <div class="desktop-title-slot" data-sv-player-title>
        ${initialTitle}
      </div>
    </div>
    <div class="desktop-controls"></div>
    <div class="desktop-right">
      <div class="desktop-actions">
        <button class="action-btn copy-link-btn" aria-label="Copy track link">
          <i class="fas fa-share-alt"></i>
        </button>

        <a href="#"
           class="action-btn purchase-btn"
           target="_blank"
           rel="noopener"
           aria-label="Purchase or download">
          <i class="fas fa-shopping-cart"></i>
        </a>

        <a href="#"
           class="action-btn download-btn"
           aria-label="Download track">
          <i class="fas fa-download"></i>
        </a>
      </div>
    </div>
  `;

  controlsContainer.appendChild(desktopBar);

  const desktopControls = desktopBar.querySelector(".desktop-controls");
  if (desktopControls && controls.parentNode !== desktopControls) {
    desktopControls.appendChild(controls);
  }

  // If we DO have hero art at page load (song.php), seed it once
  const artSlot = desktopBar.querySelector(".desktop-art-slot");
  if (artSlot && artEl) {
    const artClone = artEl.cloneNode(true);
    artClone.classList.add("desktop-art");
    artSlot.appendChild(artClone);
  }

  // Actions wired from config
  const storeHref = links.store || links.bandcamp || links.website || null;
  const mp3Href = links.mp3 || null;
  const canDownload = availability.canDownload ?? !!mp3Href;

  const purchaseBtn = desktopBar.querySelector(".purchase-btn");
  if (purchaseBtn) {
    if (storeHref) {
      purchaseBtn.href = storeHref;
      purchaseBtn.style.display = "";
    } else {
      purchaseBtn.style.display = "none";
    }
  }

  const downloadBtn = desktopBar.querySelector(".download-btn");
  if (downloadBtn) {
    if (!canDownload || !mp3Href) {
      downloadBtn.style.display = "none";
    } else {
      downloadBtn.addEventListener("click", (event) => {
        event.preventDefault();
        openDownloadModal({ storeHref, mp3Href });
      });
    }
  }

  const copyLinkBtn = desktopBar.querySelector(".copy-link-btn");
  if (copyLinkBtn) {
    copyLinkBtn.addEventListener("click", async () => {
      const url = window.location.href;

      if (!navigator.clipboard || !navigator.clipboard.writeText) {
        window.prompt("Copy this track link:", url);
        return;
      }

      try {
        await navigator.clipboard.writeText(url);

        const bubble = document.createElement("div");
        bubble.textContent = "Link copied!";
        bubble.style.position = "absolute";
        bubble.style.bottom = "130%";
        bubble.style.right = "0";
        bubble.style.background = "rgba(0,0,0,0.7)";
        bubble.style.padding = "6px 10px";
        bubble.style.borderRadius = "6px";
        bubble.style.fontSize = "12px";
        bubble.style.whiteSpace = "nowrap";
        bubble.style.pointerEvents = "none";

        copyLinkBtn.appendChild(bubble);
        setTimeout(() => bubble.remove(), 1400);
      } catch (err) {
        console.error("[Player] Copy to clipboard failed:", err);
      }
    });
  }

  // NEW: if playback is already active, immediately push the
  // current track into this fresh desktop bar.
  if (
    window.SVPlayer &&
    typeof window.SVPlayer.syncNowPlayingUi === "function"
  ) {
    window.SVPlayer.syncNowPlayingUi();
  }
}

export function openDownloadModal({ storeHref, mp3Href }) {
  // If absolutely no store link, fall back to direct download
  if (!storeHref && mp3Href) {
    window.location.href = mp3Href;
    return;
  }

  let modal = document.querySelector(".download-modal-backdrop");
  let isNew = false;

  if (!modal) {
    isNew = true;
    modal = document.createElement("div");
    modal.className = "download-modal-backdrop";
    modal.innerHTML = `
      <div class="download-modal" role="dialog" aria-modal="true" aria-labelledby="downloadModalTitle">
        <button type="button" class="download-modal-close" aria-label="Close">
          &times;
        </button>
        <h2 id="downloadModalTitle" class="download-modal-title">Support Slim Volume</h2>
        <p class="download-modal-text">
          You can download this track directly, but we'd really appreciate it if you purchased it instead.
          Purchases unlock high-quality audio and support future Slim Volume releases.
        </p>
        <div class="download-modal-actions action-links">
          <a href="#"
             class="download-modal-purchase action-link"
             target="_blank"
             rel="noopener">
            Purchase on Bandcamp
          </a>
          <a href="#"
             class="download-modal-download action-link">
            Download MP3
          </a>
        </div>
      </div>
    `;
    modal.style.display = "none";
    document.body.appendChild(modal);
  }

  const dialog = modal.querySelector(".download-modal");
  const closeBtn = modal.querySelector(".download-modal-close");
  const purchaseLink = modal.querySelector(".download-modal-purchase");
  const downloadLink = modal.querySelector(".download-modal-download");

  const closeModal = () => {
    modal.style.display = "none";
    document.body.classList.remove("download-modal-open");
  };

  // Wire static listeners only once
  if (isNew && closeBtn && dialog) {
    closeBtn.addEventListener("click", closeModal);

    // Click on backdrop closes modal
    modal.addEventListener("click", (event) => {
      if (event.target === modal) {
        closeModal();
      }
    });

    // Escape key closes modal
    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && modal.style.display === "block") {
        closeModal();
      }
    });
  }

  // Update links for this track
  if (purchaseLink) {
    if (storeHref) {
      purchaseLink.href = storeHref;
      purchaseLink.style.display = "";
    } else {
      purchaseLink.style.display = "none";
    }
  }

  if (downloadLink) {
    if (mp3Href) {
      downloadLink.href = mp3Href;
      downloadLink.setAttribute("download", "");
      downloadLink.style.display = "";
    } else {
      downloadLink.style.display = "none";
    }
  }

  // Show modal
  modal.style.display = "block";
  document.body.classList.add("download-modal-open");
}